import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import LearnerProficiencyChart from '@/components/charts/learner-proficiency-chart/learner-proficiency-chart';
import moment from 'moment';
import DomainCard from '@/components/cards/domain-card/domain-card';
import GradeLevelCard from '@/components/cards/grade-level-card/grade-level-card';
import CompetencyCard from '@/components/cards/competency-card/competency-card';
import Axios from 'axios';
import { searchAPI } from '@/providers/apis/search/search';
import { taxonomyAPI } from '@/providers/apis/taxonomy/taxonomy';
import { getSubjectId, getDomainId, getCourseId } from '@/utils/utils';
import { GOORU_DEFAULT_FRAMEWORK, MICRO_COMPETENCY_CODE_TYPES } from '@/utils/constants';
import LearningMapContent from '@/components/cards/learning-map-content/learning-map-content';
import { MicroCompetencyModel } from '@/models/content/micro-competency';
import { competencyAPI } from '@/providers/apis/competency/competency';

@Component({
    name: 'proficiency-matrix',
    components: {
        'learner-proficiency-chart': LearnerProficiencyChart,
        'domain-card': DomainCard,
        'grade-level-card': GradeLevelCard,
        'competency-card': CompetencyCard,
        'learning-map-content': LearningMapContent,
    },
})

export default class ProficiencyMatrix extends Vue {

    @Prop()
    private subject: any;

    @Prop()
    private userId: any;

    private month: string = moment().format('MM');

    private year: string = moment().format('YYYY');

    private domainList?: any = [];

    private gradeCompetency?: any = [];

    private isDomainView: boolean = false;

    private isGradeView: boolean = false;

    private activeGrade: any = null;

    private isCompetencyView: boolean = false;

    private activeCompetency: any = null;

    private isCompetencyActive: boolean = false;

    private learningMapContent: any = [];

    private prerequisites: any = [];

    private microCompetency: MicroCompetencyModel[] | any = [];

    private fwCode: string = GOORU_DEFAULT_FRAMEWORK;

    private microCompetencyCodes: any = MICRO_COMPETENCY_CODE_TYPES;

    private isLoading: boolean = false;

    private isLearningMapContent: boolean = false;

    private selectedContent: string = '';

    private selectedDomain: any = {};

    private selectedCompetency: any = {};

    private isGradeSelect: boolean = false;

    private activeTopic: any = null;

    private gutCode: any = null;

    private includeContentTypes = ['signature-collection', 'course', 'unit', 'lesson', 'collection', 'assessment', 'collection', 'offline-activity', 'question', 'resource', 'signature-assessment', 'signature-collection'];

    get subjectId() {
        return this.activeCompetency ? getSubjectId(this.activeCompetency.competencyCode) : '';
    }

    get domainId() {
        return this.activeCompetency ? getDomainId(this.activeCompetency.competencyCode) : '';
    }

    get courseId() {
        return this.activeCompetency ? getCourseId(this.activeCompetency.competencyCode) : '';
    }


    @Watch('activeCompetency')
    private onChangeCompetencyCode(code: any) {
        this.gutCode = code.competencyCode;
        this.fetchLearningMapContent(code.competencyCode);
    }

    // -----------------------------------------------------------------
    // Hooks


    // -----------------------------------------------------------------
    // Action

    private loadingDomain(domains: any) {
        this.domainList = domains;
        this.isDomainView = true;
        this.isGradeView = false;
        this.isCompetencyView = false;
    }

    private backAction() {
        this.$emit('backAction');
    }

    private onSelectGrade(competencies: [], activeGrade: any) {
        this.activeGrade = activeGrade;
        this.gradeCompetency = competencies;
        this.isDomainView = false;
        this.isGradeView = true;
        this.isCompetencyView = false;
    }

    private onCloseGrade(isClosed: boolean = false) {
        const isclosedGrade = !isClosed && this.isGradeSelect;
        this.isDomainView = isclosedGrade ? false : true;
        this.isGradeView = isclosedGrade ? true : false;
        this.isCompetencyView = false;
        this.isCompetencyActive = false;
        if (this.isDomainView && this.isGradeView) {
            this.isGradeSelect = false;
        }
    }

    private onSelectCompetency(competency: any, isGrade: boolean = false) {
        this.isGradeSelect = isGrade;
        this.isDomainView = false;
        this.isGradeView = false;
        this.isCompetencyView = true;
        this.activeCompetency = competency;
        this.isCompetencyActive = true;
        this.gutCode = this.activeCompetency.competencyCode;
    }

    private onChangeGraphView(isActive: boolean) {
        this.isCompetencyActive = isActive;
        if (!this.isCompetencyView) {
            this.isCompetencyActive = false;
        }
    }

    private onSelectContent(content: string) {
        this.selectedContent = content;
        this.gutCode = this.activeCompetency.competencyCode;
        this.isLearningMapContent = true;
    }

    private onCloseLearningMap() {
        this.selectedContent = '';
        this.isLearningMapContent = false;
    }

    private onSelectDomain(domain: any) {
        if (!domain) {
            this.selectedDomain = null;
            return;
        }
        const isActive = this.selectedDomain && domain.domainSeq === this.selectedDomain.domainSeq;
        this.selectedDomain = (!isActive || this.isGradeSelect && !this.isGradeView) ? domain : null;
    }

    private onSelectTopic(topic: any) {
        this.activeTopic = topic;
        this.activeGrade = topic;
        this.gradeCompetency = topic.competencies;
        this.isDomainView = false;
        this.isGradeView = true;
        this.isCompetencyView = false;
    }

    // -----------------------------------------------------------------------------
    // Methods

    private fetchLearningMapContent(competencyCode: any) {
        this.isLearningMapContent = false;
        this.isLoading = true;
        this.prerequisites = [];
        const frameworkId = this.subject.frameworkId || this.fwCode;
        const subjectId = `${frameworkId}.${this.subjectId}`;
        const courseId = `${frameworkId}.${this.courseId}`;
        const domainId = `${frameworkId}.${this.domainId}`;
        const competencyPromise = taxonomyAPI.fetchCodes(frameworkId, subjectId, courseId, domainId);
        const searchContent = {
            gutCodes: [competencyCode],
            includeContentTypes: this.selectedContent ? this.includeContentTypes : this.includeContentTypes,
            languageId: 1,
        };
        const searchCapALLContentPromise = searchAPI.searchCapLearnerContents(searchContent);
        Axios.all([
            searchAPI.fetchLearningMapPrerequisiteContents(competencyCode),
            searchCapALLContentPromise,
            competencyPromise,
        ]).then(Axios.spread((learningMapPrerequisite, learningMap, competencyCodes: any) => {
            this.learningMapContent = learningMap;
            this.microCompetency = this.filterMicroCompetency(competencyCodes);
            const prerequisitesList = learningMapPrerequisite.competencyPrerequisites ?
                learningMapPrerequisite.competencyPrerequisites : [];
            (async () => {
                for await (const item of prerequisitesList) {
                    for await (const domain of this.domainList) {
                        for await (const topic of domain.topics) {
                            const foundCompetency = topic.competencies.find(
                                (comp: any) => comp.competencyCode === item);
                            if (foundCompetency) {
                                const parseItem: any = {
                                    code: foundCompetency.competencyDisplayCode,
                                    id: foundCompetency.competencyCode,
                                    title: foundCompetency.competencyName,
                                };
                                this.prerequisites.push(parseItem);
                            }
                        }
                    }
                }
            })();

            this.prerequisites = this.prerequisites.reverse();
            this.isLoading = false;
        }));
    }

    private filterMicroCompetency(codes: any) {
        const standardCode = this.activeCompetency.competencyCode;
        const regex = new RegExp(standardCode);
        const microCompetencies = codes.filter((code: any) => {
            return (
                regex.test(code.id) &&
                (this.microCompetencyCodes.indexOf(code.codeType) !== -1)
            );
        });
        return microCompetencies;
    }
    private refreshData(code: any) {
        const month = Number(this.month);
        const year = Number(this.year);
        const params = {
          user: this.userId,
          subject: this.subject.code,
          month,
          year,
        };
        competencyAPI.fetchDomainTopicCompetencyMatrix(params).then((res) => {
            const currentDomain = res.find((domain) => domain.domainCode  ===  this.selectedDomain.domainCode);
            if (currentDomain) {
                const currentTopic = currentDomain.topics.find((topic) =>
                    topic.topicCode === this.activeCompetency.topicCode,
                );
                if (currentTopic) {
                    const currentComp = currentTopic && currentTopic.competencies ?
                    currentTopic.competencies.find((comp) =>
                        comp.competencyCode === this.activeCompetency.competencyCode,
                    ) : null;
                    if (currentComp) {
                        currentComp.topicCode = this.activeCompetency.topicCode;
                        currentComp.domainCode =  this.selectedDomain.domainCode;
                        this.activeCompetency = currentComp;
                    }
                }
            }
        });
    }

}
